import { useLayoutEffect, useRef, useState } from 'react';

import { stripHtmlTags } from '@worldathletics/utilities';
import type { ImageBlock } from '@worldathletics/types';

import { sanitizeBlockHtml } from '@/lib/sanitize';
import { useUploadAssetMutation } from '@/query/assets/use-presign-upload';

import { AltTextModal } from './alt-text-modal';
import { ImageKebabMenu } from './image-kebab-menu';
import { useImageResize } from './use-image-resize';

import './document-editor-image.scss';

export type DocumentEditorImageProps = {
  block: ImageBlock;
  bookId: string;
  onChange: (patch: Partial<ImageBlock>) => void;
  onDelete: () => void;
  selected: boolean;
  onSelect: () => void;
};

function DocumentEditorImage(props: DocumentEditorImageProps) {
  const { block, bookId, onChange, onDelete, selected, onSelect } = props;
  const [uploading, setUploading] = useState(false);
  const [altModalOpen, setAltModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const upload = useUploadAssetMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const captionRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const node = captionRef.current;
    if (!node) return;
    // Only sync from prop if the cell isn't focused (avoid clobbering live typing)
    if (document.activeElement !== node) {
      // Render as HTML so imported bold/italic styling (e.g. "Figure N")
      // displays instead of the raw tags; this editor has no rich-text
      // toolbar, so a genuine edit (below) still saves back as plain text.
      node.innerHTML = sanitizeBlockHtml(block.caption ?? '');
    }
  }, [block.caption]);

  function handleCaptionBlur() {
    const previousPlainCaption = stripHtmlTags(block.caption ?? '').trim();
    const value = captionRef.current?.textContent?.trim() ?? '';
    if (value !== previousPlainCaption) {
      onChange({ caption: value });
    }
  }

  async function handleFile(file: File): Promise<void> {
    setUploading(true);
    try {
      const result = await upload.mutateAsync({ file, bookId });
      onChange({
        src: result.src,
        assetUrl: result.assetUrl,
        width: result.width,
        height: result.height,
      });
    } finally {
      setUploading(false);
    }
  }

  const alignmentClass = `image-block image-block--${block.alignment}`;
  const hasImage = Boolean(block.assetUrl ?? block.src);
  const displaySrc = block.assetUrl ?? block.src;

  const resize = useImageResize({
    initialWidth: block.width ?? 240,
    initialHeight: block.height ?? 160,
    onChange: size => onChange(size),
  });

  return (
    <div
      id={block.id}
      className={`${alignmentClass} ${selected ? 'image-block--selected' : ''} ${isMenuOpen ? 'is-menu-open' : ''}`}
      onClick={onSelect}
      role="figure"
      aria-label={block.alt ?? 'image'}
    >
      {uploading && <div className="image-block__spinner">Uploading…</div>}
      {!uploading && hasImage && (
        <img
          src={displaySrc}
          alt={block.alt ?? ''}
          width={block.width}
          height={block.height}
          className="image-block__img"
        />
      )}
      {!uploading && !hasImage && (
        <button
          type="button"
          className="image-block__placeholder"
          onClick={() => fileInputRef.current?.click()}
        >
          Click to upload an image
        </button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          e.target.value = '';
        }}
      />
      <div
        ref={captionRef}
        className="image-block__caption"
        contentEditable
        suppressContentEditableWarning
        data-placeholder="Add caption (optional)"
        onBlur={handleCaptionBlur}
      />

      {selected && hasImage && (
        <>
          <span
            className="image-block__handle image-block__handle--bl"
            onMouseDown={e => resize.startDrag(e.nativeEvent, 'left')}
          />
          <span
            className="image-block__handle image-block__handle--br"
            onMouseDown={e => resize.startDrag(e.nativeEvent, 'right')}
          />
        </>
      )}

      {selected && hasImage && (
        <ImageKebabMenu
          currentAlignment={block.alignment}
          onReplace={() => fileInputRef.current?.click()}
          onAlignLeft={() => onChange({ alignment: 'left' })}
          onAlignCenter={() => onChange({ alignment: 'center' })}
          onAlignRight={() => onChange({ alignment: 'right' })}
          onEditAlt={() => setAltModalOpen(true)}
          onDelete={onDelete}
          onOpenChange={setIsMenuOpen}
        />
      )}
      <AltTextModal
        isOpen={altModalOpen}
        initialValue={block.alt ?? ''}
        onSave={v => onChange({ alt: v })}
        onClose={() => setAltModalOpen(false)}
      />
    </div>
  );
}

export { DocumentEditorImage };
